import type { BillableBreakdown as BillableBreakdownType } from '../types'

interface BillableBreakdownProps {
  data: BillableBreakdownType
}

const REASON_LABELS: Record<string, string> = {
  REPEAT_CALL: 'Repeat Call',
  CUSTOMER_RETURN: 'Customer Return',
  ZERO_DURATION_AND_SHORT_RINGING: 'Zero Duration',
  ACCEPTED_INTENT: 'Accepted Intent',
}

export function BillableBreakdown({ data }: BillableBreakdownProps) {
  const billablePct = data.total > 0 ? (data.billable / data.total) * 100 : 0
  const unbillablePct = data.total > 0 ? (data.unbillable / data.total) * 100 : 0

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Billable Breakdown</h3>

      {/* Donut-style bar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-6 rounded-full bg-gray-100 overflow-hidden flex">
          <div
            className="h-full bg-green-500 transition-all duration-500"
            style={{ width: `${billablePct}%` }}
          />
          <div
            className="h-full bg-red-400 transition-all duration-500"
            style={{ width: `${unbillablePct}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-sm text-gray-600">
            Billable: <span className="font-semibold">{data.billable}</span>{' '}
            <span className="text-gray-400">({data.billable_rate}%)</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <span className="text-sm text-gray-600">
            Unbillable: <span className="font-semibold">{data.unbillable}</span>{' '}
            <span className="text-gray-400">({(100 - data.billable_rate).toFixed(1)}%)</span>
          </span>
        </div>
      </div>

      {data.unbillable_reasons.length > 0 && (
        <>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Unbillable Reasons
          </h4>
          <div className="space-y-2">
            {data.unbillable_reasons.map((reason) => (
              <div key={reason.reason} className="flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {REASON_LABELS[reason.reason] || reason.reason}
                </span>
                <span className="text-sm font-medium text-gray-700">{reason.count}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
